import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Select, Spinner } from "@resideo/blueprint-react";
import apiService from "@/services/apiService";
import { permissionService } from "@/services/permissionService";
import {
  PRO_PORTAL_REDIRECT_FEATURE_FLAG,
  PRO_PORTAL_URL,
} from "@/config/proPortal";
import styles from "./ContextSelection.module.css";

interface ContextInfo {
  context: string;
  displayName: string;
}

const contextMetadata: Record<string, { displayName: string }> = {
  RESIDENTIAL: { displayName: "contextSelection.residential" },
  COMMERCIAL: { displayName: "contextSelection.smallBusiness" },
  PARTNER: { displayName: "contextSelection.serviceProfessional" },
};

export const ContextSelection: React.FC = () => {
  const { user, selectContext } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  if (!user || !user.contexts || user.contexts.length === 0) {
    navigate("/login");
    return null;
  }

  // Build context info with metadata
  const contextOptions: ContextInfo[] = user.contexts.map((ctx) => {
    const metadata = contextMetadata[ctx.context] || {
      displayName: ctx.contextDisplayName || ctx.context,
    };

    return {
      context: ctx.context,
      displayName: metadata.displayName || ctx.contextDisplayName || ctx.context,
    };
  });

  const openProPortal = (url: string) => {
    const newWindow = window.open(url, "_blank");
    if (newWindow) {
      newWindow.opener = null;
    }
  };

  const handleServiceProfessionalClick = async () => {
    if (!PRO_PORTAL_URL) {
      console.error("VITE_PRO_PORTAL_URL is not configured.");
      return;
    }

    try {
      const partnerUser = await apiService.getMyPartnerUser();
      await apiService.startAobSession(
        partnerUser.partnerAccountId,
        partnerUser.id,
      );
      openProPortal(PRO_PORTAL_URL);
    } catch (error) {
      console.error("Failed to start Pro Portal redirect session", error);
      openProPortal(PRO_PORTAL_URL);
    }
  };

  const handleSelectContext = async (context: string) => {
    console.log("🔘 Context button clicked:", context);
    console.log("📊 Current state:", {
      user: !!user,
      contexts: user?.contexts?.length,
    });

    try {
      console.log("⏳ Calling selectContext...");
      setIsLoading(true);
      if (
        context === "PARTNER" &&
        permissionService.isFeatureEnabled(PRO_PORTAL_REDIRECT_FEATURE_FLAG)
      ) {
        await handleServiceProfessionalClick();
        return;
      }

      await selectContext(context);
      console.log("✅ selectContext completed, navigation should happen now");
    } catch (error) {
      console.error("❌ Error selecting context:", error);
      alert(`Error selecting context: ${error}`);
    } finally {
      // Reset loading so the user is not stuck on a spinner when selection
      // does not navigate away (e.g. blocked PARTNER context shows a modal
      // and keeps the user on this page).
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
              <h1 className={styles.heading}>{t('contextSelection.heading')}</h1>

        <div className={styles.cardBody}>
          {isLoading ? (
            <div className={styles.spinnerWrapper}>
              <Spinner size="large" />
            </div>
          ) : (
          <Select
            name="context-select"
            defaultValue=""
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              const value = e.target.value;
              if (value) {
                handleSelectContext(value);
              }
            }}
          >
            <option value="" disabled>
              {t('contextSelection.placeholder')}
            </option>
            {contextOptions.map((option) => (
              <option key={option.context} value={option.context}>
                {t(option.displayName)}
              </option>
            ))}
          </Select>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContextSelection;